import { useMemo } from "react"
import type { CanvasPayload, CanvasState } from "../types"
import { HistoryStrip } from "./HistoryStrip"
import { CanvasPayloadRouter } from "./CanvasPayloadRouter"
import styles from "../styles/widgetFin.module.css"

/** A left-pointing chevron — back-nav affordance in the canvas header.
 *  Same stroke recipe as the close X and other widget icons. */
const BackArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" />
  </svg>
)

/** Friendly label for the canvas header. Driven by the active payload kind
 *  so the header reflects what the customer is looking at right now —
 *  "Building your kit" while Pearl asks the next axis question, "Your kit"
 *  once the summary card is up, etc. Falls back to a quiet "Canvas" when
 *  nothing is staged. */
function labelForPayload(payload: CanvasPayload | null): string {
  if (!payload) return "Canvas"
  switch (payload.kind) {
    case "basket":
      return "Your Cart"
    case "build_step":
      return "Building your kit"
    case "build_summary":
      return "Your kit"
    case "product":
    case "products":
      return "Product"
    case "color":
    case "colors":
      return "Color match"
    case "video":
      return "Watch"
    case "tracking":
      return "Order status"
    case "knowledge":
      return "Guide"
    case "size_guide":
      return "How much you need"
    default:
      return "Canvas"
  }
}

export function CanvasShell({
  canvas,
  onUserReply,
  onAddToCart,
  onRemoveItem,
  onQtyChange,
  onCheckout,
  onSelectPayload,
  onClose,
}: {
  canvas: CanvasState
  onUserReply: (text: string) => void
  onAddToCart: (kitVariantId: number, addOnNames: string[]) => void
  onRemoveItem: (itemId: string) => void
  onQtyChange: (itemId: string, qty: number) => void
  onCheckout: () => void
  onSelectPayload: (id: string) => void
  onClose: () => void
}) {
  // "Back" target: the most-recent payload in history that ISN'T the
  // currently-active one. With mergeHistory pushing newest-first AND the
  // active payload sitting at history[0], the back target is history[1].
  // Hide the back button when nothing's behind us (first canvas turn).
  const backTarget = useMemo<CanvasPayload | null>(() => {
    if (!canvas.activePayload) return null
    const activeId = canvas.activePayload.id
    return canvas.history.find((p) => p.id !== activeId) ?? null
  }, [canvas.activePayload, canvas.history])

  return (
    <aside className={styles["canvas"]} aria-label="Pearl canvas">
      <header className={styles["canvas-header"]}>
        {backTarget && (
          <button
            type="button"
            className={styles["canvas-back"]}
            onClick={() => onSelectPayload(backTarget.id)}
            aria-label="Back to previous canvas view"
            title="Back"
          >
            <BackArrow />
          </button>
        )}
        <span className={styles["canvas-header-title"]}>
          {labelForPayload(canvas.activePayload)}
        </span>
        <button
          type="button"
          className={styles["canvas-close"]}
          onClick={onClose}
          aria-label="Close canvas"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      </header>
      <div className={styles["canvas-body"]}>
        {canvas.activePayload ? (
          // key={payload.id} forces React to unmount the prior payload's
          // tree and mount the new one fresh — which restarts the slide-in
          // keyframe so each new result animates in cleanly instead of
          // appearing instantly under the previous content.
          <div
            key={canvas.activePayload.id}
            className={styles["canvas-payload-enter"]}
          >
            <CanvasPayloadRouter
              payload={canvas.activePayload}
              basket={canvas.basket}
              onUserReply={onUserReply}
              onAddToCart={onAddToCart}
              onRemoveItem={onRemoveItem}
              onQtyChange={onQtyChange}
              onCheckout={onCheckout}
            />
          </div>
        ) : (
          <div className={styles["canvas-empty"]}>
            <span>Nothing here yet — Pearl will surface results.</span>
            <button type="button" className={styles["canvas-empty-back"]} onClick={onClose}>
              Back to chat
            </button>
          </div>
        )}
      </div>
      <HistoryStrip
        payloads={canvas.history}
        activeId={canvas.activePayload?.id ?? null}
        onSelect={onSelectPayload}
      />
    </aside>
  )
}
